/**
 * Dienstverband Section (Epic 5 — Uit-dienst-registratie, Story 5.2)
 *
 * Sectie op de medewerker-detailpagina: "Uit dienst melden"-knop (actief),
 * of de grijze status-chip + terugdraai-flow (uit dienst). Uitgelicht als
 * eigen component (i.p.v. inline in `EmployeeDetailPage.tsx`, dat al zeer
 * groot is) zodat dit HR-gevoelige, zichtbare gedrag geïsoleerd getest kan
 * worden — zelfde precedent als `NiveauOverrideSection.tsx` — zie
 * `DienstverbandSection.test.tsx`.
 *
 * Designsysteem (NFR-1, bindend): status-chip en "Uit dienst melden"-knop
 * zijn GEEN fouten — grijs/teal, nooit rood. Rood uitsluitend bij een echte
 * servervalidatiefout na een terugdraai-poging.
 */
import { useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { useRevertUitDienst } from './useEmployees';
import { UitDienstDialog } from './components/UitDienstDialog';
import { HoverTooltip } from '../../components/HoverTooltip';
import { ApiRequestError } from '../../lib/api';
import { formatDutchDate } from '../../lib/dateUtils';

interface DienstverbandSectionProps {
  employeeId: number;
  employeeName: string;
  uitDienstDatum: string | null;
  canRevertUitDienst: boolean;
}

export function DienstverbandSection({ employeeId, employeeName, uitDienstDatum, canRevertUitDienst }: DienstverbandSectionProps) {
  const { hasRole } = useAuth();
  const canManageUitDienst = hasRole(['admin', 'hr']);

  const revertUitDienst = useRevertUitDienst(employeeId);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [showRevertConfirm, setShowRevertConfirm] = useState(false);
  const [revertError, setRevertError] = useState<string | null>(null);

  async function handleRevert() {
    // Review-fix: re-entry-guard tegen een dubbele klik vóór de eerste
    // re-render (symmetrisch aan UitDienstDialog.handleSubmit).
    if (revertUitDienst.isPending) return;
    setRevertError(null);
    try {
      await revertUitDienst.mutateAsync();
      setShowRevertConfirm(false);
    } catch (error) {
      setRevertError(error instanceof ApiRequestError ? error.message : 'Er is een onbekende fout opgetreden');
    }
  }

  function openRevertConfirm() {
    // Review-fix: begin een nieuwe terugdraai-poging altijd zonder een
    // eventueel achtergebleven foutbericht van een eerdere, geannuleerde of
    // mislukte poging (anders blijft een rode foutmelding staan nadat de
    // gebruiker al heeft geannuleerd).
    setRevertError(null);
    setShowRevertConfirm(true);
  }

  function cancelRevertConfirm() {
    setRevertError(null);
    setShowRevertConfirm(false);
  }

  return (
    <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-6" data-testid="dienstverband-section">
      <h2 className="text-lg font-medium text-gray-900 mb-4">Dienstverband</h2>

      {uitDienstDatum ? (
        <div className="flex flex-col gap-3">
          <span
            data-testid="uit-dienst-status-chip"
            className="inline-flex items-center w-fit px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700"
          >
            Uit dienst per {formatDutchDate(uitDienstDatum)}
          </span>

          {revertError && (
            <p data-testid="revert-uit-dienst-error" className="text-sm text-[#D64545]">
              {revertError}
            </p>
          )}

          {canManageUitDienst &&
            (canRevertUitDienst ? (
              showRevertConfirm ? (
                <div data-testid="revert-uit-dienst-confirm" className="flex flex-col gap-2">
                  <p className="text-sm text-gray-600">
                    Weet u zeker dat u de uit-dienst-registratie van <strong>{employeeName}</strong> ongedaan wilt maken?
                    Het gesloten tijdvak wordt hersteld en de Nmbrs-koppeling weer actief gezet.
                  </p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      data-testid="revert-uit-dienst-confirm-yes"
                      onClick={handleRevert}
                      disabled={revertUitDienst.isPending}
                      className="px-3 py-1.5 text-sm font-medium text-white rounded-md bg-[#2F5A7A] hover:bg-[#2F5A7A]/90 disabled:opacity-50"
                    >
                      {revertUitDienst.isPending ? 'Bezig...' : 'Ja, terugdraaien'}
                    </button>
                    <button
                      type="button"
                      data-testid="revert-uit-dienst-confirm-cancel"
                      onClick={cancelRevertConfirm}
                      disabled={revertUitDienst.isPending}
                      className="px-3 py-1.5 text-sm font-medium text-gray-700 rounded-md border border-[#E2E8F0] hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Annuleren
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  data-testid="revert-uit-dienst-link"
                  onClick={openRevertConfirm}
                  className="text-sm text-[#54949E] hover:underline w-fit"
                >
                  Uit-dienst-registratie ongedaan maken
                </button>
              )
            ) : (
              <HoverTooltip
                content="Er is al een beoordelingsronde gestart op of na deze datum. Terugdraaien is niet meer mogelijk."
                testId="revert-uit-dienst-disabled-tooltip"
              >
                {/* Review-fix (a11y): tabIndex/role/aria-disabled zodat
                    toetsenbord-only gebruikers deze uitleg ook kunnen
                    bereiken -- HoverTooltip toont ook op focus, maar een
                    niet-focusbaar element kon nooit focus krijgen. */}
                <span
                  data-testid="revert-uit-dienst-disabled"
                  tabIndex={0}
                  role="button"
                  aria-disabled="true"
                  className="text-sm text-gray-400 cursor-not-allowed w-fit focus:outline-none focus:ring-2 focus:ring-gray-300 rounded"
                >
                  Uit-dienst-registratie ongedaan maken
                </span>
              </HoverTooltip>
            ))}
        </div>
      ) : (
        canManageUitDienst && (
          <button
            type="button"
            data-testid="uit-dienst-open-dialog"
            onClick={() => setIsDialogOpen(true)}
            className="px-4 py-2 text-sm font-medium text-white rounded-md bg-[#54949E] hover:bg-[#54949E]/90"
          >
            Uit dienst melden
          </button>
        )
      )}

      <UitDienstDialog isOpen={isDialogOpen} onClose={() => setIsDialogOpen(false)} employeeId={employeeId} />
    </div>
  );
}
